const STABILITY_CHECK_DURATION_MS = 400;

interface PageReadinessOptions {
	stabilityDuration?: number
	skipStabilityCheck?: boolean
}

/**
 * Ensures the page is fully loaded and stable before allowing dynamic interactions.
 * This function performs two key checks:
 * 1. Basic page load: Waits for `DOMContentLoaded` and `window.load` events
 * 2. Layout stability: Monitors DOM mutations to ensure dynamic content has settled
 *
 * @param options - Configuration options for the detection process
 *   - stabilityDuration: How long to wait for layout stability (default: 400ms)
 *   - skipStabilityCheck: Whether to skip the layout stability check
 * @returns Promise that resolves when the page is ready for interaction
 */
export async function waitForPageReadiness(options: PageReadinessOptions = {}): Promise<void> {
	const {
		stabilityDuration = STABILITY_CHECK_DURATION_MS,
		skipStabilityCheck = false,
	} = options;

	await waitForBasicPageLoad();

	if (!skipStabilityCheck) {
		await waitForLayoutStability(stabilityDuration);
	}
}

/**
 * Checks if an element is visible in the DOM (rendered, taking up space, and not hidden by CSS).
 * An element is considered visible if it has dimensions, is not hidden by CSS properties,
 * and is not set to display:none or visibility:hidden.
 *
 * @param element - The HTML element to check for visibility
 * @returns true if the element is visible and can be interacted with, false otherwise
 */
export function isElementVisible(element: HTMLElement): boolean {
	const hasDimensions = !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);

	if (!hasDimensions) {
		return false;
	}

	const style = window.getComputedStyle(element);

	if (style.display === "none" || style.visibility === "hidden" || parseFloat(style.opacity) === 0) {
		return false;
	}

	return true;
}

/**
 * Waits for the basic page load to complete by ensuring both `DOMContentLoaded` and `window.load` events have fired.
 * This ensures the initial HTML document is fully loaded and parsed, and all resources (images, stylesheets, etc.)
 * have finished loading.
 */
async function waitForBasicPageLoad(): Promise<void> {
	if (document.readyState === "loading") {
		await new Promise((resolve) => {
			document.addEventListener("DOMContentLoaded", resolve, { once: true });
		});
	}

	if (document.readyState !== "complete") {
		await new Promise((resolve) => {
			window.addEventListener("load", resolve, { once: true });
		});
	}
}

/**
 * Waits for the page layout to stabilize by monitoring DOM mutations.
 * This is particularly useful for handling dynamic content loading and layout shifts,
 * such as `vue-inline-svg` components that initially render as empty comment nodes `<!-- -->`
 * before loading actual SVG content. See: https://dev.miaplaza.com/miaplaza/website/-/issues/17341
 *
 * The method creates a timer that resets whenever a DOM mutation occurs.
 * Once no mutations have occurred for the specified duration, the promise resolves,
 * indicating that the layout has stabilized.
 *
 * @param stabilityDuration - The time in milliseconds to wait without mutations before considering the layout stable
 * @returns Promise that resolves when the layout has been stable for the specified duration
 */
async function waitForLayoutStability(stabilityDuration: number = STABILITY_CHECK_DURATION_MS): Promise<void> {
	return new Promise((resolve) => {
		let timeoutHandle: number;

		const resetTimer = () => {
			clearTimeout(timeoutHandle);
			timeoutHandle = window.setTimeout(() => {
				observer.disconnect();
				resolve();
			}, stabilityDuration);
		};

		const observer = new MutationObserver(() => {
			resetTimer();
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["class", "style"],
		});

		resetTimer();
	});
}
